import * as React from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { IDropdownProps } from '../dropdown-props';
import { ListView, IListViewProps } from '../../listview';
import { ICalloutProps, Callout } from '../../callout';
import { Typography } from '../../typography';
import { Textbox } from '../../textbox';
import { DropdownState } from '../theme';

interface IOptionProps {
    isGrouped?: boolean;
    optionState: OptionState;
    highlightItemOnSelect?: boolean;
    onClick?(event?: React.MouseEvent): void;
}

interface IOptionsProps {
    renderSelectAll: boolean;
    selectable: 'multiple' | 'single';
    selectedKeys: React.Key[];
    onChange(keys: React.Key[]): void;
}

interface IOptionRendererProps extends ICalloutProps, IOptionsProps,
    Pick<IListViewProps, 'canSelectItem' | 'loadOnDemand' | 'initialItems' | 'onFetchMoreItems' | 'itemsIterator'>,
    Pick<IDropdownProps, 'items' | 'onRenderItem' | 'searchPlaceHolder' | 'fitOptionWidthToFieldWidth' | 'hideSearchIcon'>,
    Pick<ICalloutProps, 'disableRefocusTarget'> {
    itemHeight: number;
    searchFieldRef: React.RefObject<HTMLInputElement>;
    showSearchField: boolean;
    handleKeyboardNavigationOnSearch(ev: React.KeyboardEvent): void;
    onSearchValueChange(searchText: string): void;
}

export enum OptionState {
    default,
    focused,
    hovered,
    selected,
    selectedAndHovered,
    selectedAndFocused
}

const getDropdownOptionStylesByState = memoizeFunction((state: DropdownState) => {
    const getStyles = (theme: DefaultTheme) => {
        const { dropdown: { colors } } = theme;
        return colors[state];
    };
    return css`
        background-color: ${({ theme }) => getStyles(theme).optionBackgroundColor};
        color: ${({ theme }) => getStyles(theme).optionTextColor};
    `;
});

function ListBase(props: IListViewProps & IOptionsProps) {
    const {
        selectable = 'multiple',
        selectedKeys = [],
        items,
        onChange,
        onRenderItem,
        className,
        elementRef,
        canSelectItem,
        itemHeight,
        itemsIterator,
        loadOnDemand,
        onFetchMoreItems
    } = props;
    return (
        <ListView
            elementRef={elementRef}
            className={className}
            selectable={selectable}
            itemHeight={itemHeight}
            allowKeyboardNavigation
            selectedKeys={selectedKeys}
            onChange={onChange}
            isVirtualized
            keyProp="key"
            onRenderItem={onRenderItem}
            canSelectItem={canSelectItem}
            itemsIterator={itemsIterator}
            loadOnDemand={loadOnDemand}
            items={items}
            onFetchMoreItems={onFetchMoreItems} />
    );
}

const List = styled(ListBase) <{ maxHeight?: number; }>`
    position: inherit;
    display: block;
    min-width: 100%;
    box-sizing: border-box;
    height: ${({ items = [], itemHeight = 32 }) => (items.length * itemHeight)}px;
    max-height: ${({ maxHeight }) => (maxHeight ? `${maxHeight}px` : '')};
    border: none;
    &:focus {
        outline: 0;
    }
`;

export const StyledLabel = styled.label`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
`;

export const Option = styled(Typography.Small).attrs(() => ({
    className: String('ap-dropdown-option')
})) <IOptionProps>`
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    
    ${StyledLabel} {
        cursor: pointer;
    }

    ${({ optionState, highlightItemOnSelect }) => {
        const isSelectedAndFocused = optionState === OptionState.selectedAndFocused;
        optionState = !highlightItemOnSelect && isSelectedAndFocused ? OptionState.focused : optionState;
        if (highlightItemOnSelect || (!highlightItemOnSelect && optionState) === OptionState.focused) {
            switch (optionState) {
                case OptionState.selected:
                    return getDropdownOptionStylesByState('selected');
                case OptionState.focused:
                    return getDropdownOptionStylesByState('focused');
                case OptionState.selectedAndFocused:
                    return getDropdownOptionStylesByState('selectAndFocused');
                default:
                    return getDropdownOptionStylesByState('default');
            }
        }
        return undefined;
    }};
    :hover {
         ${({ optionState, highlightItemOnSelect }) => {
        if (!highlightItemOnSelect) {
            return getDropdownOptionStylesByState('hovered');
        }
        return optionState !== OptionState.selected ? getDropdownOptionStylesByState('hovered') : '';
    }};
    };
`;

export const OptionGroup = styled(Typography.H5).attrs(() => ({
    className: String('ap-dropdown-optionGroup')
}))`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    font-weight: 600;
    color: ${({ theme: { dropdown: { colors } } }) => colors.default.optionGroupTextColor};
    padding: ${({ theme: { semantics: { gap } } }) => `${gap.none} ${gap.xxl}`};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
`;

interface IOptionDividerProps extends React.HTMLProps<HTMLDivElement> {
    $topPosition: number;
}

export const OptionDivider = styled.div.attrs(() => ({
    className: String('ap-dropdown-optionDivider')
})) <IOptionDividerProps>`
    width: 100%;
    height: 1px;
    pointer-events: none;
    top: ${({ $topPosition }) => $topPosition}px;
    background-color: ${({ theme: { dropdown: { colors } } }) => colors.dividerColor};
`;

const ContentWrapper = styled.div`
    position: relative;
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    overflow: auto;
    z-index: 5;
`;

const StyledCallout = styled(Callout)`
    box-shadow: ${({ theme: { semantics: { boxShadow } } }) => boxShadow.m};
    border: ${({ theme: { dropdown: { composites } } }) => composites.calloutBorderWidth};
`;

const StyledTextbox = styled(Textbox)`
    width: 100%;
`;

const SEARCH_FIELD_ITEM_HEIGHT = 40;
const REGEX_TO_EXTRACT_NUMERIC = /^\d+/;
function getCalloutMaxHeight(calloutRef: React.RefObject<HTMLElement>) {
    const { current } = calloutRef;
    if (current === null) {
        return SEARCH_FIELD_ITEM_HEIGHT;
    }
    const maxHeightValue = getComputedStyle(current).maxHeight;
    const matchingResult = maxHeightValue.match(REGEX_TO_EXTRACT_NUMERIC);
    return (matchingResult === null || matchingResult[0] === undefined) ? SEARCH_FIELD_ITEM_HEIGHT : Number(matchingResult[0]);
}

export function Options(props: IOptionRendererProps) {
    const {
        targetRef,
        elementRef,
        hidden,
        onDismiss,
        items,
        renderSelectAll = true,
        onChange,
        onRenderItem,
        searchPlaceHolder,
        onSearchValueChange,
        searchFieldRef,
        showSearchField,
        selectable,
        selectedKeys,
        canSelectItem,
        fitOptionWidthToFieldWidth,
        itemHeight,
        handleKeyboardNavigationOnSearch,
        itemsIterator,
        loadOnDemand,
        onFetchMoreItems,
        hideSearchIcon,
        disableRefocusTarget
    } = props;
    const calloutRef = React.useRef(null);
    const [listMaxHeight, setListMaxHeight] = React.useState(itemHeight);

    const updateCalloutMaxHeight = React.useCallback(() => {
        setListMaxHeight(getCalloutMaxHeight(calloutRef) - SEARCH_FIELD_ITEM_HEIGHT);
    }, []);

    return (
        <StyledCallout
            className="ap-dropdown-options"
            targetRef={targetRef}
            hidden={hidden}
            useTargetsWidth={fitOptionWidthToFieldWidth}
            onDismiss={onDismiss}
            animateOnOpen={false}
            elementRef={calloutRef}
            onOpen={updateCalloutMaxHeight}
            onResize={updateCalloutMaxHeight}
            disableRefocusTarget={disableRefocusTarget}
            closeOnEscape
            autofocusOnOpen
            preventClickEventPropagation>
            <ContentWrapper>
                {
                    showSearchField && (
                        <StyledTextbox
                            autoFocus
                            placeholder={searchPlaceHolder}
                            onValueChange={onSearchValueChange}
                            onKeyDown={handleKeyboardNavigationOnSearch}
                            elementRef={searchFieldRef}
                            iconSize="20"
                            gap="8"
                            iconName={hideSearchIcon ? undefined : 'ap-search2.outline'}
                            iconPosition={hideSearchIcon ? undefined : 'left'} />
                    )
                }

                <List
                    itemHeight={itemHeight}
                    renderSelectAll={renderSelectAll}
                    onChange={onChange}
                    onRenderItem={onRenderItem}
                    selectable={selectable}
                    selectedKeys={selectedKeys}
                    canSelectItem={canSelectItem}
                    elementRef={elementRef}
                    maxHeight={listMaxHeight}
                    itemsIterator={itemsIterator}
                    loadOnDemand={loadOnDemand}
                    items={items}
                    onFetchMoreItems={onFetchMoreItems} />
            </ContentWrapper>
        </StyledCallout>
    );
}
